import { Module } from 'vuex';
import { Contact } from '@/modules/contacts/shared/domain/contact';
import { UserGetters } from '@/modules/users/store/user.getters';
import ticketsRepository from '@/modules/tickets/shared/repositories/tickets.repository';
import { User } from '@/modules/users/domain/user';
import { Tickets } from '@/modules/shared/shared/domain/constants/constants';
import { Typing, TypingLevels } from '@/modules/tickets/shared/domain/typing';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { CategoryTyping } from '@/modules/tickets/shared/domain/category-typing';
import { EventBus } from '@/main';
import { EventBusMessages } from '@/modules/shared/shared/domain/constants/event-bus-messages';
import { ManagementManagedServicesState } from '@/modules/tickets/requirement/management-managed-services/store/management-managed-services.state';
import { ManagementManagedServicesGetters } from '@/modules/tickets/requirement/management-managed-services/store/management-managed-services.getters';
import { ManagementManagedServicesMutations } from '@/modules/tickets/requirement/management-managed-services/store/management-managed-services.mutations';
import { ManagementManagedServicesActions } from '@/modules/tickets/requirement/management-managed-services/store/management-managed-services.actions';
import { ManagementManagedServicesData } from '@/modules/tickets/requirement/management-managed-services/domain/management-managed-services-data';
import { ManagementManagedServiceTicket } from '@/modules/tickets/requirement/management-managed-services/domain/management-managed-service-ticket';

const managementAndManagedServices: Module<ManagementManagedServicesState, any> = {
    namespaced: true,
    state: {
        formValues: {
            generalInformation: {
                category: null,
                subCategory: null,
                subject: null,
            },
            categoriesTypings: [],
        },
    },
    getters: {
        [ManagementManagedServicesGetters.GET_FORM_VALUES](
            state,
        ): {
            generalInformation: ManagementManagedServicesData;
            categoriesTypings: CategoryTyping;
        } {
            return state.formValues;
        },
    },
    mutations: {
        [ManagementManagedServicesMutations.SET_FORM_VALUES](state, formValues) {
            state.formValues = formValues;
        },
        [ManagementManagedServicesMutations.SET_CONTACT_INFORMATION](state, contact: Contact) {
            state.formValues.generalInformation.firstName = contact.name;
            state.formValues.generalInformation.lastName = contact.lastName;
            state.formValues.generalInformation.contactId = contact.id;
            state.formValues.generalInformation.idNumber = contact.ci;
            state.formValues.generalInformation.telephoneNumber = contact.phone;
        },
        [ManagementManagedServicesMutations.SET_TYPINGS](state, typings: Typing[]) {
            state.formValues.categoriesTypings = typings.filter(
                typing => typing.level === TypingLevels.CATEGORY_LEVEL,
            );
            const subjects = typings.filter(typing => typing.level === TypingLevels.SUBJECT_LEVEL);
            const subCategories = typings.filter(
                typing => typing.level === TypingLevels.SUBCATEGORY_LEVEL,
            );

            state.formValues.categoriesTypings = state.formValues.categoriesTypings.map(
                category => {
                    return {
                        ...category,
                        subCategoriesTypings: subCategories
                            .filter(subCategory => subCategory.typing === category.id)
                            .map(subCategory => {
                                return {
                                    ...subCategory,
                                    subjectTypings: subjects.filter(
                                        subject => subject.typing === subCategory.id,
                                    ),
                                };
                            }),
                    };
                },
            );
        },
    },
    actions: {
        async [ManagementManagedServicesActions.FETCH_VIEW]({
            commit,
            rootGetters,
        }): Promise<RequestResponse> {
            commit(ManagementManagedServicesMutations.SET_FORM_VALUES, {
                generalInformation: {
                    category: null,
                    subCategory: null,
                    subject: null,
                },
                categoriesTypings: [],
            });
            const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
            commit(ManagementManagedServicesMutations.SET_CONTACT_INFORMATION, currentUser.contact);
            try {
                const typings = await ticketsRepository.getTypings(
                    currentUser.contact.customer.id,
                    Tickets.ManagementAndManagedServices.name,
                );
                commit(ManagementManagedServicesMutations.SET_TYPINGS, typings);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },

        async [ManagementManagedServicesActions.CREATE_TICKET](
            { rootGetters },
            information: ManagementManagedServicesData,
        ): Promise<RequestResponse> {
            try {
                const currentUser: User = rootGetters[`user/${UserGetters.GET_CURRENT_USER}`];
                const contact = currentUser.contact;
                const customer = contact.customer;
                const ticket: ManagementManagedServiceTicket = {
                    description: information.description || '',
                    typing: information.subject!,
                    ticketType: Tickets.ManagementAndManagedServices,
                    customer: {
                        id: customer.id,
                        name: customer.name,
                        category: customer.category!,
                        contact: {
                            id: contact.id,
                            name: contact.name,
                            lastName: contact.lastName,
                            email: contact.email,
                            ci: contact.ci!,
                        },
                    },
                };
                const id = (await ticketsRepository.createTicket(ticket)).data.id;
                EventBus.$emit(
                    EventBusMessages.SHOW_SUCCESS,
                    `Su ticket fue creado exitosamente (Serial Nº ${id})`,
                );
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
    },
};

export default managementAndManagedServices;
